import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AppointmentStatusPill } from './appointment-status-pill';
import { Appointment, APPOINTMENT_ORIGIN, APPOINTMENT_STATUS } from '@/types/appointment';
import { formatSlotRange, formatSlotTime } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

export type ConflictKind = 'grid' | 'block' | null;

interface Props {
  appointment: Appointment;
  conflict?: ConflictKind;
  occupancy?: { count: number; capacity: number };
  onPress?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  actionPending?: boolean;
  /** Agenda da equipe: de quem é o horário (ponto da cor + primeiro nome). */
  professional?: { name: string; color: string | null };
}

const CONFLICT_LABEL: Record<'grid' | 'block', string> = {
  grid: 'Fora da grade',
  block: 'Em horário bloqueado',
};

export function ProviderAppointmentCard({
  appointment,
  conflict,
  occupancy,
  onPress,
  onApprove,
  onReject,
  actionPending,
  professional,
}: Props) {
  const th = useThemedHex();
  const pending = appointment.status === APPOINTMENT_STATUS.PENDING;
  const isRecurrence = appointment.origin === APPOINTMENT_ORIGIN.RECURRENCE;

  return (
    <PressableScale
      onPress={onPress}
      className="flex-row overflow-hidden rounded-2xl border bg-card"
      style={{
        borderCurve: 'continuous',
        borderColor: conflict ? '#FCA5A5' : '#F0F0F0',
      }}
    >
      {/* Faixa de horário */}
      <View
        className="items-center justify-center px-3 py-4"
        style={{ backgroundColor: conflict ? th('#FEF2F2') : th('#FAFAFA'), minWidth: 68 }}
      >
        <ThemedText className="font-bold text-base text-neutral-900">
          {formatSlotTime(appointment.startAt)}
        </ThemedText>
        <ThemedText className="text-[11px] text-neutral-400">
          {formatSlotTime(appointment.endAt)}
        </ThemedText>
      </View>

      {/* Conteúdo */}
      <View className="min-w-0 flex-1 gap-1.5 p-3">
        <View className="flex-row items-start justify-between gap-2">
          <ThemedText
            className="min-w-0 flex-1 font-semibold text-sm text-neutral-900"
            numberOfLines={1}
          >
            {appointment.clientName ?? 'Cliente'}
          </ThemedText>
          <AppointmentStatusPill status={appointment.status} />
        </View>

        <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
          {appointment.serviceName ?? 'Serviço'} ·{' '}
          {formatSlotRange(appointment.startAt, appointment.endAt)}
        </ThemedText>

        <View className="flex-row flex-wrap items-center gap-1.5">
          {professional && (
            <View className="flex-row items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5">
              <View
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: professional.color ?? '#FD5001' }}
              />
              <ThemedText className="font-semibold text-[10px] text-neutral-600">
                {professional.name}
              </ThemedText>
            </View>
          )}
          {occupancy && occupancy.capacity > 1 && (
            <View className="flex-row items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5">
              <Ionicons className="text-neutral-600" name="people-outline" size={11} />
              <ThemedText className="font-semibold text-[10px] text-neutral-600">
                {occupancy.count}/{occupancy.capacity}
              </ThemedText>
            </View>
          )}
          {isRecurrence && (
            <View className="flex-row items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5">
              <Ionicons name="repeat" size={11} color="#4F46E5" />
              <ThemedText className="font-semibold text-[10px] text-indigo-600">
                Recorrência
              </ThemedText>
            </View>
          )}
          {appointment.isOverbook && (
            <View className="flex-row items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5">
              <Ionicons className="text-amber-700" name="flash" size={11} />
              <ThemedText className="font-semibold text-[10px] text-amber-700">Encaixe</ThemedText>
            </View>
          )}
          {conflict && (
            <View className="flex-row items-center gap-1 rounded-full bg-red-50 px-2 py-0.5">
              <Ionicons className="text-red-700" name="warning-outline" size={11} />
              <ThemedText className="font-semibold text-[10px] text-red-700">
                {CONFLICT_LABEL[conflict]}
              </ThemedText>
            </View>
          )}
        </View>

        {/* Ações rápidas de pendente (C4) */}
        {pending && (onApprove || onReject) && (
          <View className="mt-1 flex-row gap-2">
            {onApprove && (
              <PressableScale
                haptic
                disabled={actionPending}
                onPress={onApprove}
                className="flex-1 flex-row items-center justify-center gap-1 rounded-full bg-primary py-2"
              >
                {actionPending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Ionicons name="checkmark" size={15} color="#fff" />
                    <ThemedText className="font-bold text-xs text-white">Aprovar</ThemedText>
                  </>
                )}
              </PressableScale>
            )}
            {onReject && (
              <PressableScale
                haptic
                disabled={actionPending}
                onPress={onReject}
                className="flex-1 flex-row items-center justify-center gap-1 rounded-full border border-neutral-200 py-2"
              >
                <Ionicons className="text-neutral-600" name="close" size={15} />
                <ThemedText className="font-semibold text-xs text-neutral-700">Recusar</ThemedText>
              </PressableScale>
            )}
          </View>
        )}
      </View>
    </PressableScale>
  );
}
